
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiRefreshCw, FiEdit2, FiTrash2, FiSave, FiX, FiAlertCircle 
} from 'react-icons/fi';
import { services } from '../../../../services/api';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import useBreakpoint from '../../../../hooks/useBreakpoint';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import Loader from "../../../../components/Loader";

//--> Volume format pill. Shared by the desktop table and the phone cards so the
//    two presentations can't drift.
const VolumeFormatBadge = ({ format }) => (
  <span className={`ds-badge sm ${format === 'DELTA' ? 'info' : 'accent'}`}>{format}</span>
);

//--> Where the setting came from. Manual = someone overrode it (warn), Auto =
//    the engine detected it (profit). Colour carries the meaning, so: badges.
const SourceBadge = ({ manual }) => (
  manual ? (
    <span className="ds-badge sm warn"><FiEdit2 size={10} /> Manual</span>
  ) : (
    <span className="ds-badge sm profit"><FiRefreshCw size={10} /> Auto</span>
  )
);

const VolumeSettingsList = () => {
  const { showSnackbar } = useSnackbar();
  //! Phone gets CARDS, not the table. This is a 7-column, ~1000px-wide table
  //! whose Edit/Delete live in the LAST column behind 28px targets 8px apart —
  //! on a 375px screen that means panning ~650px right for every row and then
  //! aiming between two adjacent tiny icons. Desktop keeps the table.
  const { isPhone } = useBreakpoint();

  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ volume_format: '' });
  
  // Confirmation Modal State
  const [confirmModalState, setConfirmModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {}
  });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await services.volumeSettings.list();
      setSettings(response.data.settings || []);
    } catch (error) {
      console.error('Failed to load volume settings:', error);
      showSnackbar('Failed to load volume settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleEdit = (setting) => {
    setEditingId(setting.id);
    setEditForm({ volume_format: setting.volume_format });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ volume_format: '' });
  };

  const handleSave = async (id) => {
    try {
      await services.volumeSettings.override(id, { volume_format: editForm.volume_format });
      showSnackbar('Volume setting updated', 'success');
      setEditingId(null);
      fetchSettings();
    } catch (error) {
      console.error('Failed to update setting:', error);
      showSnackbar('Failed to update setting', 'error');
    }
  };

  const handleDelete = (id, broker, exchange) => {
    setConfirmModalState({
      isOpen: true,
      title: 'Delete Volume Setting?',
      message: `Are you sure you want to delete the setting for ${broker}/${exchange}? It will be auto-detected again if data flows.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await services.volumeSettings.delete(id);
          showSnackbar('Setting deleted', 'success');
          fetchSettings();
        } catch (error) {
           console.error('Failed to delete setting:', error);
           showSnackbar('Failed to delete setting', 'error');
        } finally {
           setConfirmModalState(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  //--> Shared empty state for both presentations.
  const emptyMessage = 'No volume settings found. They will appear as data is processed.';

  //--> PHONE: one card per config. Broker/Exchange head it, the four read-only
  //    facts sit in a labelled grid, and the actions are real, well-separated
  //    buttons instead of 28px icons hiding off the right edge of a wide table.
  //    Inline edit mode is preserved: the Volume Format cell swaps to a select
  //    and the action pair swaps to Save/Cancel, exactly like the table.
  const renderSettingCards = () => (
    <div className="flex flex-col gap-3">
      {settings.length === 0 ? (
        <div className="rounded-xl border border-[var(--divider)] bg-bg-2 px-4 py-12 text-center text-fg-3">
          {emptyMessage}
        </div>
      ) : (
        settings.map(setting => (
          <div key={setting.id} className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-fg-1 capitalize">{setting.broker_code}</div>
              <div className="truncate text-sm text-fg-2">{setting.exchange}</div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3 border-t border-[var(--divider)] pt-3">
              <div>
                <span className="mb-1 block text-xs font-semibold text-fg-3">Instrument Type</span>
                <span className="text-sm text-fg-2">{setting.instrument_type || 'ALL'}</span>
              </div>
              <div>
                <span className="mb-1 block text-xs font-semibold text-fg-3">Source</span>
                <SourceBadge manual={setting.manually_overridden} />
              </div>
              <div>
                <span className="mb-1 block text-xs font-semibold text-fg-3">Confidence</span>
                <span className="text-sm text-fg-2">
                  {setting.manually_overridden ? '—' : (
                    setting.confidence ? `${(setting.confidence * 100).toFixed(0)}%` : 'N/A'
                  )}
                </span>
              </div>
              <div>
                <span className="mb-1 block text-xs font-semibold text-fg-3">Volume Format</span>
                {editingId === setting.id ? (
                  <select
                    value={editForm.volume_format}
                    onChange={(e) => setEditForm({ volume_format: e.target.value })}
                    className="ds-input block w-full"
                  >
                     <option value="DELTA">DELTA</option>
                     <option value="CUMULATIVE">CUMULATIVE</option>
                  </select>
                ) : (
                  <VolumeFormatBadge format={setting.volume_format} />
                )}
              </div>
            </div>

            <div className="mt-4 flex gap-3 border-t border-[var(--divider)] pt-3">
              {editingId === setting.id ? (
                <>
                  <button
                    onClick={() => handleSave(setting.id)}
                    className="btn btn-primary inline-flex flex-1 items-center justify-center gap-2"
                  >
                    <FiSave size={16} /> Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="btn btn-secondary inline-flex flex-1 items-center justify-center gap-2"
                  >
                    <FiX size={16} /> Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleEdit(setting)}
                    className="btn btn-secondary inline-flex flex-1 items-center justify-center gap-2"
                  >
                    <FiEdit2 size={16} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(setting.id, setting.broker_code, setting.exchange)}
                    className="btn btn-danger inline-flex flex-1 items-center justify-center gap-2"
                  >
                    <FiTrash2 size={16} /> Delete
                  </button>
                </>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Toolbox — stacks below sm: the title, a full-sentence subtitle and the
          refresh button collided at 375px. */}
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between sm:items-center bg-bg-2 p-4 rounded-xl shadow-sm border border-[var(--divider)]">
         <div>
             <h2 className="ds-h2 text-fg-1">Broker Volume Configuration</h2>
             <p className="text-sm text-fg-2 mt-1">Configure how volume data is interpreted by the Fusion Engine.</p>
         </div>
         <button
           onClick={fetchSettings}
           className="p-2.5 border border-[var(--divider)] rounded-lg text-fg-2 hover:bg-bg-3 transition-colors shrink-0"
           title="Refresh"
         >
           <FiRefreshCw className={loading ? 'animate-spin' : ''} />
         </button>
      </div>

       {/* Explanation Banner — the copy block is a <div>, not a <p>: a <p> cannot
           contain a <ul>, so the parser auto-closed it and the closing sentence
           escaped the paragraph and rendered as an orphan node. */}
       <div className="bg-[var(--badge-info-bg)] border border-[var(--divider)] rounded-xl p-4 flex items-start gap-3">
          <FiAlertCircle className="text-[var(--badge-info-fg)] mt-0.5 flex-shrink-0" size={18} />
          <div className="text-sm text-[var(--badge-info-fg)] space-y-1">
              <p className="font-semibold">How this affects Data Aggregation:</p>
              <div>
                  These settings control how the <strong>Fusion Engine</strong> normalizes volume from different brokers.
                  <ul className="list-disc ml-4 mt-1 space-y-0.5 opacity-90">
                      <li><strong>DELTA</strong>: Tick volume represents shares traded <em>only</em> in that specific tick update.</li>
                      <li><strong>CUMULATIVE</strong>: Tick volume represents total shares traded for the day; the engine calculates the difference.</li>
                  </ul>
                  <div className="mt-1">Setting this incorrectly will result in corrupted OHLC volume bars.</div>
              </div>
          </div>
       </div>

      {loading && (
        <div className="flex justify-center p-8">
           <Loader text="Loading settings..." />
        </div>
      )}

      {/* Cards on a phone, table on desktop. */}
      {!loading && isPhone && renderSettingCards()}

      {!loading && !isPhone && (
        <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[var(--divider)] bg-bg-3/50 dark:bg-bg-2/20">
                   <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider">Broker</th>
                   <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider">Exchange</th>
                   <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider">Instrument Type</th>
                   <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider">Volume Format</th>
                   <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider">Source</th>
                   <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider">Confidence</th>
                   <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--divider)]">
                {settings.length === 0 ? (
                    <tr>
                       <td colSpan="7" className="py-12 text-center text-fg-3">
                          {emptyMessage}
                       </td>
                    </tr>
                ) : (
                    settings.map(setting => (
                      <tr key={setting.id} className="group hover:bg-bg-3/30 transition-colors">
                        <td className="py-4 px-6 font-medium text-fg-1 capitalize">{setting.broker_code}</td>
                        <td className="py-4 px-6 text-fg-2">{setting.exchange}</td>
                        <td className="py-4 px-6 text-fg-2">{setting.instrument_type || 'ALL'}</td>
                        
                        {/* Editable Volume Format */}
                        <td className="py-4 px-6">
                           {editingId === setting.id ? (
                             <select
                               value={editForm.volume_format}
                               onChange={(e) => setEditForm({ volume_format: e.target.value })}
                               className="ds-input"
                             >
                                <option value="DELTA">DELTA</option>
                                <option value="CUMULATIVE">CUMULATIVE</option>
                             </select>
                           ) : (
                             <VolumeFormatBadge format={setting.volume_format} />
                           )}
                        </td>

                        {/* Source Status */}
                        <td className="py-4 px-6">
                           <SourceBadge manual={setting.manually_overridden} />
                        </td>
                        
                         <td className="py-4 px-6 text-fg-2 text-xs">
                            {setting.manually_overridden ? '—' : (
                               setting.confidence ? `${(setting.confidence * 100).toFixed(0)}%` : 'N/A'
                            )}
                         </td>

                        <td className="py-4 px-6 text-right">
                           <div className="flex items-center justify-end gap-2">
                              {editingId === setting.id ? (
                                <>
                                  {/* Tokenized hovers: the old hover:bg-green-50 /
                                      hover:bg-red-50 had no dark: variant, so in the
                                      dark theme hovering flashed a near-white tile. */}
                                  <button onClick={() => handleSave(setting.id)} className="p-1.5 text-profit hover:bg-[var(--badge-profit-bg)] rounded transition-colors"><FiSave size={16}/></button>
                                  <button onClick={handleCancelEdit} className="p-1.5 text-loss hover:bg-[var(--badge-loss-bg)] rounded transition-colors"><FiX size={16}/></button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => handleEdit(setting)} className="p-1.5 text-fg-3 hover:text-accent hover:bg-[var(--badge-accent-bg)] rounded transition-colors"><FiEdit2 size={16}/></button>
                                  <button onClick={() => handleDelete(setting.id, setting.broker_code, setting.exchange)} className="p-1.5 text-fg-3 hover:text-loss hover:bg-[var(--badge-loss-bg)] rounded transition-colors"><FiTrash2 size={16}/></button>
                                </>
                              )}
                           </div>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmationModal 
         isOpen={confirmModalState.isOpen}
         onClose={() => setConfirmModalState(prev => ({ ...prev, isOpen: false }))}
         onConfirm={confirmModalState.onConfirm}
         title={confirmModalState.title}
         message={confirmModalState.message}
         type={confirmModalState.type}
         confirmText="Delete"
      />
    </div>
  );
};

export default VolumeSettingsList;
